import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import {
    clearExpiryAdjustmentItems,
    useRestockExpiredItemsMutation,
} from '@/features/slice/inventory';
import { toastError } from '@/utils';

import { Route } from '../../../../constants';
import { useAppDispatch } from '../../../../features';
import { TCreateExpiredStockAdjustment } from './schema';

export const useCreateExpiredStockAdjustment = () => {
    const [restockExpiredItems, { isLoading, isError, isSuccess, error }] =
        useRestockExpiredItemsMutation();

    const dispatch = useAppDispatch();

    const navigate = useNavigate();

    const handleSubmit = async (data: TCreateExpiredStockAdjustment) => {
        const items = data.items || [];

        const totalPurchasePrice = items.reduce(
            (acc, item) =>
                acc + (item.purchasePrice ?? 0) * (item.quantity ?? 0),
            0,
        );

        const totalUpdatedPrice = items.reduce(
            (acc, item) =>
                acc + (item.updatedPurchasePrice ?? 0) * (item.quantity ?? 0),
            0,
        );

        const totalPayableAmount = totalUpdatedPrice - totalPurchasePrice;

        const payload = {
            total_price: totalPayableAmount,
            note: data.note,
            supplier_id: data.supplier_id,
            paid_amount: data.total_paid_amount,
            items:
                data?.items.map((item) => ({
                    purchase_item_id: item.purchaseItemId,
                    quantity: item.quantity,
                    product_variation_id: item.productVariationId,
                    expiration_date: item.expirationDate,
                    purchase_price: item.updatedPurchasePrice,
                    wholesale_price: item.wholesalePrice,
                    retail_price: item.retailPrice,
                })) ?? [],
        };

        try {
            await restockExpiredItems(payload).unwrap();
            toast.success('Expired stock adjustment created successfully');
            dispatch(clearExpiryAdjustmentItems());
            navigate(Route.ADJUSTMENT_EXPIRED_PRODUCTS);
        } catch (error) {
            toastError(error, 'useCreateExpiredStockAdjustment: handleSubmit');
        }
    };

    return {
        handleSubmit,

        isSuccess,
        isError,
        isLoading,
        error,
    };
};
